import { EllaProductData } from '@/components/common/EllaCartProduct/EllaCartProduct';
import { RawEPCCCartPrice } from './Cart';
import { ProductCardData } from './Product';
import { SubscriptionTitle } from './Subscription';
import { EPCCAddress } from './User';

export enum ORDER_ITEM_STATUS_INTERNAL {
  PROCESSING = 'Processing',
  READY_FOR_FULFILLMENT = 'Ready for Fulfillment',
  PICKED = 'Picked',
  CLEANED = 'Cleaned',
  PACKED = 'Packed',
  LABEL_CREATED = 'Label Created',
  CONDITION_PENDING = 'Condition Pending',
  CONDITION_APPROVED = 'Condition Approved',
  READY_FOR_CUSTOMER_PICKUP = 'Ready for Customer Pickup',
  SHIPPED = 'Shipped',
  AT_HOME = 'At Home',
  RETURNED = 'Returned',
  RETURN_SCANNED = 'Return Scanned',
  COMPLETE_RECEIVED_BY_VIVRELLE = 'Complete - Received By Vivrelle',
  COMPLETE_PURCHASED = 'Complete - Purchased',
  COMPLETE_OTHER = 'Complete - Other',
  CANCELED = 'Canceled',
  CONDITION_DENIED = 'Condition Denied'
}

/** User-facing status */
export enum ORDER_ITEM_STATUS {
  PROCESSING = 'Processing',
  FULFILLMENT = 'Fulfillment',
  SHIPPED = 'Shipped',
  READY_FOR_CUSTOMER_PICKUP = 'Ready for Customer Pickup',
  AT_HOME = 'At Home',
  RETURN_INITIATED = 'Return Initiated',
  RETURN_SCANNED = 'Return Scanned',
  COMPLETE = 'Complete',
  CANCELED = 'Canceled'
}

export enum ORDER_STATUS {
  PROCESSING = 'Processing',
  IN_REVIEW = 'In Review',
  FULFILLMENT = 'Fulfillment',
  CONDITION = 'Condition',
  READY_FOR_CUSTOMER_PICKUP = 'Ready for Customer Pickup',
  SHIPPED = 'Shipped',
  OPEN = 'Open',
  COMPLETE = 'Complete',
  CANCELLED = 'Canceled'
}

export enum EPCC_ORDER_TYPE {
  PRODUCT = 'product',
  SUBSCRIPTION = 'subscription',
  REVOLVE = 'revolve'
}

export enum ORDER_TRACKING_STATUS {
  PRE_TRANSIT = 'PRE_TRANSIT',
  UNKNOWN = 'UNKNOWN',
  FAILURE = 'FAILURE',
  IN_TRANSIT = 'IN_TRANSIT'
}

export type EPCCOrderShippingEntry = {
  id: string;
  type: 'entry';
  tracking_number: string;
  download_link: string;
  tracking_url: string;
  tracking_type: 'shipping' | 'return';
  shipping_response_log: string;
  tracking_status: string;
};

export type OpenSearchOrderItem = {
  id: string;
  type: 'order_item';
  product_reference: string;
  order_item_status: ORDER_ITEM_STATUS_INTERNAL;
  product_id: string;
  name: string;
  sku: string;
  quantity: number;
  catalog_id: string;
  operation_notes: string;
  'main-image-url': string;
  'can-early-swap': boolean;
  'items-tracking-details': any[];
  'product-reference-sku': string;
  'product-reference-legacy-sku': string;
  'order-created-date': string;
  'item-received-by-vivrelle-date': Date;
  'subscription-plan-used': SubscriptionTitle;
  relationships: {
    cart_item?: {
      data: any;
    };
  };
  unit_price: {
    amount: number;
    currency: string;
    includes_tax: boolean;
  };
  value: {
    amount: number;
    currency: string;
    includes_tax: Boolean;
  };
  meta: {
    timestamps: {
      created_at: Date;
      updated_at: Date;
    };
  };
  catalog_source: string;
  shipping?: EPCCOrderShippingEntry;
  return?: EPCCOrderShippingEntry;
};

export type EPCCTransaction = {
  id: string;
  type: 'transaction';
  reference: string;
  gateway: string;
  amount: number;
  refunded_amount: number;
  currency: 'USD';
  transaction_type: string;
  status: string;
  relationships?: {
    order?: {
      data: {
        type: 'order';
        id: string;
      };
    };
  };
  meta: {
    display_price: RawEPCCCartPrice;
    display_refunded_amount: {
      total: RawEPCCCartPrice;
    };
    timestamps: {
      created_at: string;
      updated_at: string;
    };
  };
  next_actions: string[];
};

export type EPCCShippingMethod = {
  id: string;
  type: 'entry';
  label: string;
  value: string;
  description: string;
  price: number;
  sort_order: number;
};

export type EPCCOrder = {
  type: 'order';
  id: string;
  short_id: string;
  items: OpenSearchOrderItem[];
  shipping_address: EPCCAddress;
  billing_address: EPCCAddress;
  'order-type': EPCC_ORDER_TYPE;
  'subscription-tracking-order-id': string;
  'order-created-date': string;
  'shipping-methods'?: EPCCShippingMethod;
  'internal-order-status': ORDER_STATUS;
  relationships?: {
    customer?: {
      data: {
        type: 'customer';
        id: string;
      };
    };
    items?: {
      data: [
        {
          type: 'item';
          id: string;
        }
      ];
    };
    'shipping-methods'?: {
      data: {
        id: string;
      };
    };
  };
  meta: {
    display_price?: {
      discount: RawEPCCCartPrice;
      shipping: RawEPCCCartPrice;
      tax: RawEPCCCartPrice;
      with_tax: RawEPCCCartPrice;
      without_discount: RawEPCCCartPrice;
      without_tax: RawEPCCCartPrice;
    };
    timestamps: {
      created_at: Date;
      updated_at: Date;
    };
  };
};

export type EPCCOrderItem = {
  type: 'order_item';
  id: string;
  quantity: number;
  product_id: string;
  name: string;
  sku: string;
  custom_inputs?: {
    product: EllaProductData;
  };
};

export type EPCCOrderResponse = {
  data: EPCCOrder;
  included: {
    items?: EPCCOrderItem[];
  };
};

export type OpenSearchOrder = {
  _index: string;
  _id: string;
  _source: EPCCOrder;
};

export type OpenSearchOrderResponse = {
  status: number;
  data?: {
    data?: {
      body?: {
        hits?: {
          total: {
            value: number;
          };
          hits?: OpenSearchOrder[];
        };
      };
    };
  };
};

export type OrderItem = {
  product: ProductCardData;
  internalStatus: ORDER_ITEM_STATUS_INTERNAL;
  status: ORDER_ITEM_STATUS;
  trackingStatus?: TrackingStatus | null;
  shipping?: EPCCOrderShippingEntry;
  return?: EPCCOrderShippingEntry;
};

export type Order = {
  id: string;
  shortId: string;
  orderDate: Date;
  shippingAddress: EPCCAddress;
  orderItems: OrderItem[];
  shippingMethod?: string;
  isNYCPickup: boolean;
  isHamptonsPickup: boolean;
  isSohoPickup: boolean;
  isNyfwPickup: boolean;
  isPickup: boolean;
  status: ORDER_STATUS;
};

export type ShippingLogMessage = {
  source: string;
  code: string;
  text: string;
};

export type ShippingLog = {
  status: string;
  data: {
    object_state: string;
    status: string;
    object_created: Date;
    object_updated: Date;
    object_id: string;
    rate: string;
    tracking_number: string;
    tracking_status: string;
    eta: Date | null;
    tracking_url_provider: string;
    label_url: string;
    metadata: string;
    messages: ShippingLogMessage[];
  };
};

export type SHIPPO_TRACKING_STATUS =
  | 'PRE_TRANSIT'
  | 'TRANSIT'
  | 'DELIVERED'
  | 'RETURNED'
  | 'FAILURE'
  | 'UNKNOWN';

export type TrackingStatusAddress = {
  city: string;
  state: string;
  zip: string;
  country: string;
};

export type TrackingStatusItem = {
  status: SHIPPO_TRACKING_STATUS;
  status_details: string;
  status_date: string;
  object_created: string;
  object_updated: string;
};

export type TrackingStatus = {
  tracking_number: string;
  carrier: string;
  servicelevel: {
    name: string;
    token: string;
  };
  address_from: TrackingStatusAddress;
  address_to: TrackingStatusAddress;
  eta: Date;
  original_eta: Date;
  metadata: string;
  test: boolean;
  tracking_status?: TrackingStatusItem;
  tracking_history?: TrackingStatusItem[];
};

export type RevolveOrderItem = {
  code: string;
  size: string;
  quantity: number;
  store: 'revolve' | 'fwrd';
};

export type RevolveShippingAddress = {
  country: string;
  countryCode: string;
  name: string;
  street: string;
  street2?: string;
  city: string;
  state: string;
  zipCode: string;
  telephone: string;
};
